import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { PagedResponse, Product } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service to get and save product data.
 */
@Injectable()
export class ProductService {
    public static get INCLUDE_PRODUCT_TYPE_VERIFICATION(): string {
        return 'productTypeVerification';
    }

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
    }

    public exportToCsv(eventId: string): void {
        location.href = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/products/csv-download`;
    }

    /**
     * Get all products for the specified event
     */
    public getIndexListResponse(
        eventId: string,
        includes: string[] = null,
        queryParams: string[] = [],
    ): Observable<PagedResponse> {
        // Only fetch addOn products by default
        if (queryParams.filter((x: string) => x.lastIndexOf('isAddOn') !== -1).length === 0) {
            queryParams.push('isAddOn=1');
        }

        const apiRequestUrl: string = this.getApiUrl(eventId, null, includes, queryParams);
        return this.getIndexListResponseByUrl(eventId, apiRequestUrl);
    }

    /**
     * Get products by a url. This is used for paging through products since the response
     * provides the proper page urls
     */
    public getIndexListResponseByUrl(eventId: string, url: string): Observable<PagedResponse> {
        return this.http.get(url)
            .pipe(map((response: IJsonApiResponse) => {
                const json: any = response;

                const products: Product[] = [];
                for (const product of json.data) {
                    products.push((new Product()).deserialize(product));
                }

                return new PagedResponse(
                    json['links']['first'],
                    json['links']['last'],
                    json['links']['prev'],
                    json['links']['next'],
                    json['meta']['total'],
                    products,
                );
            }));
    }

    /**
     * Get all products for the specified event
     */
    public getIndex(eventId: string, includes: string[] = [], queryParams: string[] = []): Observable<Product[]> {
        const apiRequestUrl: string = this.getApiUrl(eventId, null, includes, queryParams);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const products: Product[] = [];
                const bodyData: any = response.data || [];
                for (const product of bodyData) {
                    products.push((new Product()).deserialize(product, response.included));
                }
                return products;
            }));
    }

    /**
     * Get a single product's data
     */
    public get(eventId: string, productId: string, includes: string[] = null): Observable<Product> {
        const apiRequestUrl: string = this.getApiUrl(eventId, productId, includes);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new Product().deserialize(response))));
    }

    /**
     * Add or update the product
     */
    public save(product: Product, includes: string[] = null): Observable<Product> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(product.eventId, product.id, includes);

        if (product.id) {
            // Update existing Product
            response = this.http.patch(apiRequestUrl, product.serialize());
        } else {
            // Add a new Product
            response = this.http.post(apiRequestUrl, product.serialize());
        }

        return response.pipe(map((response: IJsonApiResponse) => (new Product()).deserialize(response)));
    }

    /**
     * Delete the specified product.
     */
    public delete(eventId: string, productId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, productId, null);
        return this.http.delete(apiRequestUrl).pipe(map((response: IJsonApiResponse) => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(eventId: string, productId: string, includes?: string[], queryParams?: string[]): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/products/`;

        if (productId) {
            url += productId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}
